import PropTypes from 'prop-types';
import React from 'react';
import { connect as connectR } from 'react-redux';
import _ from 'lodash';
import { Button, FormControl, Row } from 'react-bootstrap';
import LargeCard from '../main/large-card';
import translate from '../translate';
import ValidatorFormGroup from '../main/validator-form-group';
import { DEF_VALIDATOR } from '../../util/validators';
import {
    ActivateRegistration,
    ResendCode,
    ResetRequestsToInitialStates,
} from '../../store/registrationSlice';
import {
    LoginAsPartner,
    LoginWithStoredCredentials,
} from '../../store/authSlice';
import { goToEndOfLogin, goToLogin } from '../../nav/navigationService';
import { PbRequest } from '../../store/pbRequest';

const VALIDATORS = {
    activationCode: {
        isValid(val) {
            return !!val && DEF_VALIDATOR.test(val);
        },
    },
};

class RegistrationActivationForm extends React.Component {
    static propTypes = {
        strings: PropTypes.object.isRequired,
        activatePartnerRequest: PropTypes.shape({
            status: PropTypes.string,
            errors: PropTypes.arrayOf(PropTypes.string),
        }).isRequired,
        resendActivationCodeRequest: PropTypes.shape({
            status: PropTypes.string,
            errors: PropTypes.arrayOf(PropTypes.string),
        }).isRequired,
        registrationEmail: PropTypes.string.isRequired,
        registrationPassword: PropTypes.string.isRequired,
        authEmail: PropTypes.string.isRequired,
        ActivateRegistration: PropTypes.func.isRequired,
        ResendCode: PropTypes.func.isRequired,
        RemoveError: PropTypes.func.isRequired,
        LoginAsPartner: PropTypes.func.isRequired,
        LoginWithStoredCredentials: PropTypes.func.isRequired,
    };

    state = {
        data: {
            activationCode: '',
        },
        verificationCodeSent: false,
        dirty: {},
        isValid: false,
    };

    getErrorMessage = () => {
        let errorMessage = null;
        let occurredError = this._resolveFirstErrorOrUndefined(
            this.props.activatePartnerRequest.errors
        );
        occurredError = occurredError
            ? occurredError
            : this._resolveFirstErrorOrUndefined(
                  this.props.resendActivationCodeRequest.errors
              );
        if (occurredError) {
            let errorString;

            if (occurredError === 'EMAIL_INVALID') {
                errorString = this.props.strings.errorEmail;
            } else if (occurredError === 'WRONG_ACTIVATION_CODE') {
                errorString = this.props.strings.errorWrongActivation;
            } else {
                errorString = this.props.strings.generalError;
            }

            errorMessage = (
                <Row>
                    <div className="alert alert-danger pull-left">
                        {' ' + errorString}
                    </div>
                </Row>
            );
        }
        return errorMessage;
    };

    _persist = (field, value) => {
        this._markDirty(field);
        const data = _.extend({}, this.state.data);
        data[field] = value;
        if (
            this._resolveFirstErrorOrUndefined(
                this.props.resendActivationCodeRequest.errors
            ) ||
            this._resolveFirstErrorOrUndefined(
                this.props.activatePartnerRequest.errors
            )
        ) {
            this.props.RemoveError();
        }
        this.setState({ data, isValid: this._isDataValid(data) });
    };

    _resendVerificationCode = () => {
        this.setState({ verificationCodeSent: true });
        const email = this.props.authEmail || this.props.registrationEmail;
        if (email) {
            this.props.ResendCode({
                data: email,
            });
            setTimeout(() => {
                this.setState({ verificationCodeSent: false });
            }, 10 * 1000);
        } else {
            goToLogin();
        }
    };

    _markDirty = (key) => {
        const newDirt = {};
        newDirt[key] = true;
        const newState = _.extend({}, this.state, {
            dirty: _.extend({}, this.state.dirty, newDirt),
        });
        this.setState(newState);
    };

    _isDataValid = (data) => {
        return Object.keys(VALIDATORS).reduce(
            (sum, fieldName) =>
                sum && VALIDATORS[fieldName].isValid(data[fieldName]),
            true
        );
    };

    makeGoogleTagsAlert = () => {
        //This is used for Analytics reasons, check WEW-737
        gtag('event', 'conversion', {
            send_to: 'AW-817821724/vHEXCNe_koABEJzw-4UD',
        });
    };

    _handleSubmit = (event) => {
        event.preventDefault();
        if (this.state.isValid) {
            const email = this.props.authEmail || this.props.registrationEmail;
            this.props.ActivateRegistration({
                data: {
                    code: this.state.data.activationCode,
                    email: email,
                },
                onSuccess: () => {
                    const username = this.props.registrationEmail;
                    const password = this.props.registrationPassword;
                    if (username && password) {
                        this.props.LoginAsPartner(
                            username,
                            password,
                            goToEndOfLogin
                        );
                    } else {
                        this.props.LoginWithStoredCredentials(goToEndOfLogin);
                    }
                },
            });
            this.makeGoogleTagsAlert();
        }
    };

    _resolveFirstErrorOrUndefined = (errors) => {
        return errors && errors[0] ? errors[0] : undefined;
    };

    render() {
        const errorMessage = this.getErrorMessage();
        return (
            <form onSubmit={this._handleSubmit}>
                <LargeCard
                    type="card-edit"
                    title={this.props.strings.title}
                    description={this.props.strings.description}
                >
                    <ValidatorFormGroup
                        label={this.props.strings.activationCode}
                        hideErrorLabel={
                            !this.state.dirty.activationCode ||
                            VALIDATORS.activationCode.isValid(
                                this.state.data.activationCode
                            )
                        }
                        helpLabel={this.props.strings.activationCodeHelp}
                        errorLabel={this.props.strings.activationCodeError}
                    >
                        <FormControl
                            type="text"
                            className={`input-text-color`}
                            value={this.state.data.activationCode}
                            name="activationCode"
                            onChange={(event) => {
                                this._persist(
                                    'activationCode',
                                    event.target.value
                                );
                            }}
                            required
                        />
                    </ValidatorFormGroup>
                    <div className="clearfix" />
                    {errorMessage}
                    <Row className="form-footer">
                        <Button
                            className="btn-default pull-left"
                            type="button"
                            disabled={this.state.verificationCodeSent}
                            onClick={this._resendVerificationCode}
                        >
                            {this.state.verificationCodeSent
                                ? this.props.strings.codeSent
                                : this.props.strings.resendVerificationCode}
                        </Button>
                        <Button
                            type="submit"
                            disabled={!this.state.isValid}
                            className="btn btn-primary pull-right"
                        >
                            {this.props.strings.activate}
                        </Button>
                    </Row>
                </LargeCard>
            </form>
        );
    }
}

export default connectR(
    (store) => ({
        activatePartnerRequest: store.pbRequest[PbRequest.ACTIVATION_REQUEST],
        resendActivationCodeRequest:
            store.pbRequest[PbRequest.RESEND_CODE_REQUEST],
        registrationEmail: store.registration.email,
        registrationPassword: store.registration.password,
        authEmail: store.auth.authentication.email,
    }),
    {
        ActivateRegistration: ActivateRegistration,
        ResendCode: ResendCode,
        RemoveError: ResetRequestsToInitialStates,
        LoginAsPartner,
        LoginWithStoredCredentials,
    }
)(translate('RegistrationActivationForm')(RegistrationActivationForm));
